/** Versioned so a future column-id change does not reuse stale pixel values. */
export const HUB_COLUMN_WIDTH_STORAGE_VERSION = "v1";

export const HUB_SELECT_COLUMN_WIDTH_PX = 40;

export const HUB_KEYBOARD_RESIZE_STEP_PX = 10;

export type HubColumnWidthScope = "persona" | "legacy";

export interface HubColumnSize {
  id: string;
  size: number;
  minSize: number;
  maxSize: number;
  enableResizing: boolean;
}

export function hubColumnWidthStorageKey(scope: HubColumnWidthScope): string {
  return `nexus.opportunityHub.columnWidths.${HUB_COLUMN_WIDTH_STORAGE_VERSION}.${scope}`;
}

export function clampHubColumnWidth(width: number, column: HubColumnSize): number {
  if (!column.enableResizing) return column.size;
  return Math.min(column.maxSize, Math.max(column.minSize, width));
}

export function resolveHubColumnWidth(
  column: HubColumnSize,
  widths: Readonly<Record<string, number>>,
): number {
  const stored = widths[column.id];
  if (stored == null) return column.size;
  return clampHubColumnWidth(stored, column);
}

export function hubOpportunityTableWidthPx(
  columns: readonly HubColumnSize[],
  widths: Readonly<Record<string, number>>,
): number {
  return columns.reduce(
    (total, column) => total + resolveHubColumnWidth(column, widths),
    HUB_SELECT_COLUMN_WIDTH_PX,
  );
}

type WidthStorage = Pick<Storage, "getItem" | "setItem">;

/**
 * Accessing `localStorage` can throw before getItem or setItem run.
 * Callers treat null as "keep the in-memory defaults".
 */
export function hubColumnWidthStorage(): WidthStorage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/**
 * Drops unknown ids, non-numbers, and fixed columns. Out-of-range numbers are clamped.
 * A missing or unreadable entry returns an empty map so the table uses defaults.
 */
export function readHubColumnWidths(
  storage: Pick<Storage, "getItem">,
  scope: HubColumnWidthScope,
  columns: readonly HubColumnSize[],
): Record<string, number> {
  let parsed: unknown;
  try {
    const raw = storage.getItem(hubColumnWidthStorageKey(scope));
    if (raw == null || raw === "") return {};
    parsed = JSON.parse(raw) as unknown;
  } catch {
    return {};
  }
  if (parsed == null || typeof parsed !== "object" || Array.isArray(parsed)) return {};

  const byId = new Map(columns.map((column) => [column.id, column]));
  const widths: Record<string, number> = {};
  for (const [id, value] of Object.entries(parsed)) {
    if (typeof value !== "number" || !Number.isFinite(value)) continue;
    const column = byId.get(id);
    if (column == null) {
      widths[id] = value;
      continue;
    }
    if (!column.enableResizing) continue;
    widths[id] = clampHubColumnWidth(value, column);
  }
  return widths;
}

export function writeHubColumnWidths(
  storage: Pick<WidthStorage, "setItem">,
  scope: HubColumnWidthScope,
  widths: Readonly<Record<string, number>>,
): void {
  try {
    storage.setItem(hubColumnWidthStorageKey(scope), JSON.stringify(widths));
  } catch {
    // Private mode or a full quota leaves the in-memory widths in place.
  }
}
